import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatChip } from "@angular/material/chips";
import { MatTooltip } from "@angular/material/tooltip";
import { ToolNameRegistryService } from "../../../../services/tool-name-registry.service";
import { CardHeaderComponent } from "../../shared/card-header/card-header.component";
import type { ApiEndpointData, BlockStatus } from "../../../../model/block.types";

/** URL segment for template rendering with placeholder highlighting. */
interface UrlPart {
  text: string;
  isPlaceholder: boolean;
}

/**
 * @deprecated api_endpoint blocks are no longer generated by the agent.
 * This component is preserved as a display-only card for potential reimplementation.
 * All execution logic (EndpointExecutionService, approval gates, param forms) has been removed.
 */
@Component({
  selector: "app-endpoint-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCard,
    MatCardContent,
    MatChip,
    MatTooltip,
    CardHeaderComponent,
  ],
  templateUrl: "./endpoint-card.component.html",
  styleUrl: "./endpoint-card.component.scss",
})
export class EndpointCardComponent {
  data = input.required<ApiEndpointData>();

  status = input.required<BlockStatus>();

  protected readonly toolNames = inject(ToolNameRegistryService);

  protected readonly secondaryMethods = computed(() =>
    (this.data().methods ?? []).filter((m) => m !== this.data().method),
  );

  protected readonly urlParts = computed<UrlPart[]>(() => this.#parseUrlParts(this.data().url));

  protected readonly resolvedParamEntries = computed(() =>
    Object.entries(this.data().resolved_params ?? {}).filter(([, v]) => v !== undefined && v !== ""),
  );

  #parseUrlParts(url: string): UrlPart[] {
    const parts: UrlPart[] = [];
    const regex = /<[^>]+>/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(url)) !== null) {
      if (match.index > lastIndex) parts.push({ text: url.slice(lastIndex, match.index), isPlaceholder: false });
      parts.push({ text: match[0], isPlaceholder: true });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < url.length) parts.push({ text: url.slice(lastIndex), isPlaceholder: false });
    return parts;
  }
}
